/**
 * MsgViewUI - 基于 Bootstrap 的信息显示组件
 * 支持在 div 标签中显示信息，提供 setInfo、getInfo 等方法
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface MsgViewOptions extends ElementOptions {
    /** 初始信息内容 */
    info?: string | string[];
    /** 标签文本 */
    label?: string;
    /** 是否显示标签 */
    showLabel?: boolean;
    /** 信息显示格式 */
    infoFormat?: (info: string | string[]) => string | string[];
    /** 是否禁用 */
    disabled?: boolean;
    /** 是否只读 */
    readonly?: boolean;
    /** 标签样式类 */
    labelClass?: string;
    /** 信息显示区域的样式类 */
    infoClass?: string;
    /** 容器样式类 */
    containerClass?: string[];
    /** 占位符文本 */
    placeholder?: string;
    /** 标签是否与信息显示区域同行显示 */
    isLabelInline?: boolean;
    /** 信息显示区域的最大高度 */
    maxHeight?: string;
    /** 是否支持滚动 */
    scrollable?: boolean;
    /** 信息类型（用于样式区分） */
    type?: 'info' | 'warning' | 'error' | 'success' | 'default';
    /** 多条信息的展示方向 */
    displayDirection?: 'horizontal' | 'vertical';
    /** 多条信息之间的分隔符 */
    separator?: string;
    /** 是否显示行号 */
    showLineNumbers?: boolean;
    /** 最大显示行数 */
    maxLines?: number;
}

export class MsgViewUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private infoDisplayElement: HTMLElement;
    private options: MsgViewOptions;
    private onChangeCallbacks: ((info: string | string[]) => void)[] = [];

    // 防止循环更新的标志
    private isUpdating: boolean = false;
    private previousInfo: string | string[] = '';

    constructor(options: MsgViewOptions = {}) {
        this.ui = bootstrapUI;
        this.options = {
            info: '',
            label: '信息显示',
            showLabel: true,
            infoFormat: (info: string | string[]) => info,
            disabled: false,
            readonly: false,
            labelClass: '',
            infoClass: '',
            containerClass: [],
            placeholder: '暂无信息',
            isLabelInline: false,
            maxHeight: 'auto',
            scrollable: false,
            type: 'default',
            displayDirection: 'vertical',
            separator: '',
            showLineNumbers: false,
            maxLines: 0,
            ...options
        };

        this.container = this.createElement();
        this.infoDisplayElement = this.container.querySelector('.msg-view-info') as HTMLElement;

        this.setupEventHandlers();
    }

    /**
     * 创建信息显示组件元素
     * @returns 包含信息显示区域的容器元素
     */
    private createElement(): HTMLElement {
        const container = this.ui.createElement({
            className: [
                'msg-viewui-container',
                `msg-viewui-${this.options.type}`,
                `msg-viewui-${this.options.displayDirection}`,
                ...(this.options.containerClass ? this.options.containerClass : []),
                ...(this.options.isLabelInline ? [] : ['flex-direction-column']),
            ],
            ...this.options
        });

        // 创建标签
        if (this.options.showLabel && this.options.label) {
            const label = this.ui.createElement({
                tag: 'label',
                className: ['form-label', 'msg-view-label',
                    ...(this.options.labelClass ? [this.options.labelClass] : []),
                ],
                textContent: this.options.label,
                attributes: {
                    for: this.options.id || ''
                }
            });
            container.appendChild(label);
        }

        // 创建信息显示区域
        const infoContainer = this.ui.createElement({
            className: [
                'msg-view-info-container',
                ...(this.options.scrollable ? ['msg-view-scrollable'] : []),
            ],
        });

        const infoDisplay = this.ui.createElement({
            tag: 'div',
            className: [
                'msg-view-info',
                ...(this.options.infoClass ? [this.options.infoClass] : []),
            ],
            attributes: {
                ...(this.options.maxHeight !== 'auto' && { style: `max-height: ${this.options.maxHeight}` }),
                ...(this.options.id && { id: this.options.id })
            },
        });

        // 设置初始内容
        this.setInitialContent(infoDisplay);

        infoContainer.appendChild(infoDisplay);
        container.appendChild(infoContainer);

        return container;
    }

    /**
     * 设置初始内容
     */
    private setInitialContent(infoDisplay: HTMLElement): void {
        const initialInfo = this.options.info || this.options.placeholder || '';
        this.renderContent(infoDisplay, initialInfo);
    }

    /**
     * 渲染内容到显示元素
     */
    private renderContent(element: HTMLElement, content: string | string[]): void {
        if (Array.isArray(content)) {
            this.renderMultiLineContent(element, content);
        } else {
            element.textContent = content;
        }
    }

    /**
     * 渲染多行内容
     */
    private renderMultiLineContent(element: HTMLElement, lines: string[]): void {
        // 清空现有内容
        element.innerHTML = '';

        // 限制显示行数
        const displayLines = (this.options.maxLines && this.options.maxLines > 0) ?
            lines.slice(0, this.options.maxLines) : lines;

        if (this.options.displayDirection === 'horizontal') {
            // 横向显示
            this.renderHorizontalContent(element, displayLines);
        } else {
            // 竖向显示
            this.renderVerticalContent(element, displayLines);
        }
    }

    /**
     * 渲染横向内容
     */
    private renderHorizontalContent(element: HTMLElement, lines: string[]): void {
        const container = this.ui.createElement({
            tag: 'div',
            className: ['msg-view-horizontal-container']
        });

        lines.forEach((line, index) => {
            const lineElement = this.ui.createElement({
                tag: 'span',
                className: ['msg-view-line', 'msg-view-line-horizontal'],
                textContent: line
            });

            if (this.options.showLineNumbers) {
                const lineNumber = this.ui.createElement({
                    tag: 'span',
                    className: ['msg-view-line-number'],
                    textContent: `${index + 1}. `
                });
                lineElement.insertBefore(lineNumber, lineElement.firstChild);
            }

            container.appendChild(lineElement);

            // 添加分隔符（除了最后一行）
            if (this.options.separator) {
                if (index < lines.length - 1) {
                    const separator = this.ui.createElement({
                        tag: 'span',
                        className: ['msg-view-separator'],
                        textContent: this.options.separator || ' | '
                    });
                    container.appendChild(separator);
                }
            }
        });

        element.appendChild(container);
    }

    /**
     * 渲染竖向内容
     */
    private renderVerticalContent(element: HTMLElement, lines: string[]): void {
        const container = this.ui.createElement({
            tag: 'div',
            className: ['msg-view-vertical-container']
        });

        lines.forEach((line, index) => {
            const lineElement = this.ui.createElement({
                tag: 'div',
                className: ['msg-view-line', 'msg-view-line-vertical'],
                textContent: line
            });

            if (this.options.showLineNumbers) {
                const lineNumber = this.ui.createElement({
                    tag: 'span',
                    className: ['msg-view-line-number'],
                    textContent: `${index + 1}. `
                });
                lineElement.insertBefore(lineNumber, lineElement.firstChild);
            }

            container.appendChild(lineElement);
        });

        element.appendChild(container);
    }

    /**
     * 设置事件处理器
     */
    private setupEventHandlers(): void {
        // 点击事件（如果需要交互）
        this.ui.on('click', this.infoDisplayElement, (e) => {
            if (this.options.disabled || this.options.readonly) return;
            // 可以在这里添加点击交互逻辑
        });

        // 焦点事件
        this.ui.on('focus', this.infoDisplayElement, (e) => {
            const currentInfo = this.getInfo();
            this.previousInfo = currentInfo;
        });
    }

    /**
     * 标准化信息内容
     */
    private normalizeInfo(info: any): string | string[] {
        try {
            if (info === null || info === undefined) {
                return '';
            }

            if (Array.isArray(info)) {
                return info.map(item => String(item));
            }

            if (typeof info !== 'string') {
                return String(info);
            }

            return info;
        } catch (error) {
            return '';
        }
    }

    /**
     * 更新信息显示
     */
    private updateInfoDisplay(info: string | string[]): void {
        if (this.infoDisplayElement) {
            const formattedInfo = this.options.infoFormat ?
                this.options.infoFormat(info) : info;
            this.renderContent(this.infoDisplayElement, formattedInfo);
        }
    }

    /**
     * 通知信息变化
     */
    private notifyChange(info: string | string[]): void {
        this.onChangeCallbacks.forEach(callback => {
            try {
                callback(info);
            } catch (error) {
                console.error('信息显示变化回调执行错误:', error);
            }
        });
    }

    /**
     * 获取当前信息
     */
    getInfo(): string | string[] {
        return this.normalizeInfo(this.infoDisplayElement.textContent);
    }

    /**
     * 设置当前信息
     */
    setInfo(info: string | string[]): void {
        const normalizedInfo = this.normalizeInfo(info);
        this.isUpdating = true;
        this.updateInfoDisplay(normalizedInfo);
        this.notifyChange(normalizedInfo);
        this.isUpdating = false;
    }

    /**
     * 追加信息
     */
    appendInfo(info: string | string[]): void {
        const currentInfo = this.getInfo();
        const newInfo = Array.isArray(currentInfo) ?
            [...currentInfo, ...(Array.isArray(info) ? info : [info])] :
            Array.isArray(info) ?
                [currentInfo, ...info] :
                [currentInfo, info];
        this.setInfo(newInfo);
    }

    /**
     * 清空信息
     */
    clearInfo(): void {
        this.setInfo('');
    }

    /**
     * 是否禁用
     */
    isDisabled(): boolean {
        return this.options.disabled || false;
    }

    /**
     * 设置禁用状态
     */
    setDisabled(disabled: boolean): void {
        this.options.disabled = disabled;
        if (disabled) {
            this.container.classList.add('msg-view-disabled');
        } else {
            this.container.classList.remove('msg-view-disabled');
        }
    }

    /**
     * 设置组件激活状态（启用/禁用整个组件）
     */
    setActive(status: boolean): void {
        if (!status) {
            // 禁用整个组件
            this.container.classList.add('forbidden-event-dom');

            // 禁用信息显示
            this.setDisabled(true);
        } else {
            // 启用整个组件
            this.container.classList.remove('forbidden-event-dom');

            // 启用信息显示
            this.setDisabled(false);
        }
    }

    /**
     * 是否只读
     */
    isReadOnly(): boolean {
        return this.options.readonly || false;
    }

    /**
     * 设置只读状态
     */
    setReadOnly(readonly: boolean): void {
        this.options.readonly = readonly;
        if (readonly) {
            this.container.classList.add('msg-view-readonly');
        } else {
            this.container.classList.remove('msg-view-readonly');
        }
    }

    /**
     * 设置信息类型
     */
    setType(type: 'info' | 'warning' | 'error' | 'success' | 'default'): void {
        // 移除旧类型样式
        this.container.classList.remove(`msg-viewui-${this.options.type}`);

        // 添加新类型样式
        this.options.type = type;
        this.container.classList.add(`msg-viewui-${type}`);
    }

    /**
     * 获取信息类型
     */
    getType(): string {
        return this.options.type || 'default';
    }

    /**
     * 设置最大高度
     */
    setMaxHeight(maxHeight: string): void {
        this.options.maxHeight = maxHeight;
        if (this.infoDisplayElement) {
            this.infoDisplayElement.style.maxHeight = maxHeight;
        }
    }

    /**
     * 设置是否可滚动
     */
    setScrollable(scrollable: boolean): void {
        this.options.scrollable = scrollable;
        const infoContainer = this.container.querySelector('.msg-view-info-container');
        if (infoContainer) {
            if (scrollable) {
                infoContainer.classList.add('msg-view-scrollable');
            } else {
                infoContainer.classList.remove('msg-view-scrollable');
            }
        }
    }

    /**
     * 设置显示方向
     */
    setDisplayDirection(direction: 'horizontal' | 'vertical'): void {
        this.options.displayDirection = direction;

        // 更新容器样式类
        this.container.classList.remove('msg-viewui-horizontal', 'msg-viewui-vertical');
        this.container.classList.add(`msg-viewui-${direction}`);

        // 重新渲染内容
        const currentInfo = this.getInfo();
        this.updateInfoDisplay(currentInfo);
    }

    /**
     * 获取显示方向
     */
    getDisplayDirection(): string {
        return this.options.displayDirection || 'vertical';
    }

    /**
     * 设置分隔符
     */
    setSeparator(separator: string): void {
        this.options.separator = separator;
        const currentInfo = this.getInfo();
        this.updateInfoDisplay(currentInfo);
    }

    /**
     * 获取分隔符
     */
    getSeparator(): string {
        return this.options.separator || '\n';
    }

    /**
     * 设置是否显示行号
     */
    setShowLineNumbers(show: boolean): void {
        this.options.showLineNumbers = show;
        const currentInfo = this.getInfo();
        this.updateInfoDisplay(currentInfo);
    }

    /**
     * 是否显示行号
     */
    isShowLineNumbers(): boolean {
        return this.options.showLineNumbers || false;
    }

    /**
     * 设置最大显示行数
     */
    setMaxLines(maxLines: number): void {
        this.options.maxLines = maxLines;
        const currentInfo = this.getInfo();
        this.updateInfoDisplay(currentInfo);
    }

    /**
     * 获取最大显示行数
     */
    getMaxLines(): number {
        return this.options.maxLines || 0;
    }

    /**
     * 添加单行信息
     */
    addLine(line: string): void {
        const currentInfo = this.getInfo();
        const newInfo = Array.isArray(currentInfo) ?
            [...currentInfo, line] :
            [currentInfo, line];
        this.setInfo(newInfo);
    }

    /**
     * 移除指定行
     */
    removeLine(index: number): void {
        const currentInfo = this.getInfo();
        if (Array.isArray(currentInfo) && index >= 0 && index < currentInfo.length) {
            const newInfo = currentInfo.filter((_, i) => i !== index);
            this.setInfo(newInfo);
        }
    }

    /**
     * 获取行数
     */
    getLineCount(): number {
        const currentInfo = this.getInfo();
        return Array.isArray(currentInfo) ? currentInfo.length : 1;
    }

    /**
     * 添加信息变化监听器
     */
    onChange(callback: (info: string | string[]) => void): void {
        this.onChangeCallbacks.push(callback);
    }

    /**
     * 移除信息变化监听器
     */
    offChange(callback: (info: string | string[]) => void): void {
        const index = this.onChangeCallbacks.indexOf(callback);
        if (index > -1) {
            this.onChangeCallbacks.splice(index, 1);
        }
    }

    /**
     * 获取信息显示元素
     */
    getInfoElement(): HTMLElement {
        return this.infoDisplayElement;
    }

    /**
     * 获取容器元素
     */
    getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 聚焦信息显示区域
     */
    focus(): void {
        this.infoDisplayElement.focus();
    }

    /**
     * 失焦信息显示区域
     */
    blur(): void {
        this.infoDisplayElement.blur();
    }

    /**
     * 销毁组件
     */
    destroy(): void {
        this.onChangeCallbacks = [];

        // 移除事件监听器
        this.ui.off('click', this.infoDisplayElement, () => { });
        this.ui.off('focus', this.infoDisplayElement, () => { });

        // 移除DOM元素
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}

// 导出默认实例创建函数
export function createMsgViewUI(options: MsgViewOptions = {}): MsgViewUI {
    return new MsgViewUI(options);
}

export function createDefaultMsgViewUI(): MsgViewUI {
    return new MsgViewUI({
        info: '',
        label: '',
        showLabel: true,
        infoFormat: (info: string | string[]) => info,
        disabled: false,
        readonly: false,
        labelClass: '',
        infoClass: '',
        containerClass: [],
        placeholder: '',
        isLabelInline: false,
        maxHeight: 'auto',
        scrollable: false,
        type: 'default',
        displayDirection: 'vertical',
        separator: '',
        showLineNumbers: false,
        maxLines: 0,
    });
}

